/// <amd-module name="Build/Run/Components/ChangesPanel" />
import * as React from "react";

import * as Resources from "Build/Run/Resources";
import { Panel } from "VSSUI/Panel";
import { ISourceProvider, ILinkData } from "Build/SourceProviders/Common";
import { IVssPageContext } from "VSS/Platform/Context";
import { Change } from "Build/Client/Contracts/Build";
import { format } from "VSS/Core/Util/String";
import { ScrollableList, IListItemDetails, ListItem } from "VSSUI/List";
import { IVssComponentContext, css } from "VSS/Platform/Layout";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { getAvatarUrl } from "Build/Common/Library/Identity";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { BuildPersona } from "Build/Common/Library/Components/BuildPersona";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { VssPersonaSize } from "VSSUI/VssPersona";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { Ago } from "VSSUI/Ago";
import { Icon } from "VSSUI/Icon";

export interface IChangesPanelProps {
    changes: Change[];
    changeLinks: ILinkData[];
    onDismiss: () => void;
    repositoryName: string;
    repositoryType: string;
    sourceProvider: ISourceProvider;
}

const telemetrySource = "build-changes-panel";
const defaultCommitIconName = "BranchCommit";

export class ChangesPanel extends React.PureComponent<IChangesPanelProps, {}> {
    private _itemProvider: ArrayItemProvider<Change>;

    constructor(props: IChangesPanelProps, context: IVssComponentContext) {
        super(props, context);

        this._itemProvider = new ArrayItemProvider<Change>(this.props.changes);
    }

    public render() {
        return (
            <Panel
                className="changes-panel"
                description={format(Resources.CommitsFormat, this._itemProvider.length)}
                escDismiss={true}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.AssociatedChanges }}
            >
                <ScrollableList
                    itemProvider={this._itemProvider}
                    outerClassName="custom-scrollbar flex-column"
                    renderRow={this._renderRow}
                    width="100%"
                />
            </Panel>
        );
    }

    private _renderRow = (rowIndex: number, item: Change, details: IListItemDetails<Change>): JSX.Element => {
        const changeLink = this.props.changeLinks[rowIndex];
        return (
            <ListItem key={"list-item" + rowIndex} index={rowIndex} details={details}>
                {this._getChangeElement(item, changeLink ? changeLink.commitLink : "")}
            </ListItem>
        );
    };

    private _getChangeElement(change: Change, changeLink: string) {
        const author = change.author as IdentityRef | null;
        let imageUrl = "";
        let authorId = "";
        if (author) {
            imageUrl = getAvatarUrl(author);
            authorId = author.id;
            if (!authorId) {
                authorId = author.uniqueName;
            }
        }

        if (!imageUrl && author) {
            imageUrl = getIdentityImageUrl(this.context.pageContext, author);
        }

        const commitText = change.id;
        let commitIcon = defaultCommitIconName;
        if (this.props.sourceProvider) {
            commitIcon = this.props.sourceProvider.getCommitIconName();
        }

        const displayName = author ? author.displayName : "";
        return (
            <div className="changes-row flex-center flex-row">
                <PersonaInformation
                    id={authorId}
                    imageUrl={imageUrl}
                    displayName={displayName}
                    getPrimaryElement={this._getPrimaryElement}
                    data={change}
                    changeLink={changeLink}
                >
                    <span id="build-commit-desc">
                        <span>
                            {format(Resources.AuthoredFormat, displayName)}
                            &nbsp;
                            <Icon iconName={commitIcon} />
                            &nbsp;
                            {commitText}
                            &nbsp;
                        </span>
                        {change.timestamp && <Ago date={change.timestamp} format={AgoFormat.Compact} />}
                    </span>
                </PersonaInformation>
            </div>
        );
    }

    private _getPrimaryElement = (change: Change, changeLink: string): JSX.Element => {
        return (
            <SmartCommitLink
                change={change}
                repositoryName={this.props.repositoryName}
                sourceProvider={this.props.sourceProvider}
                textToDisplay={change.message || change.id}
                ariaDescribedBy="build-commit-desc"
                changeLink={changeLink}
            />
        );
    };
}

interface IPersonaInformationProps {
    data: Change;
    displayName: string;
    getPrimaryElement: (data: Change, changeLink: string) => JSX.Element;
    id: string;
    imageUrl: string;
    sizeClassName?: VssPersonaSize;
    changeLink: string;
}

class PersonaInformation extends React.PureComponent<IPersonaInformationProps, {}> {
    public render() {
        const sizeClassName = this.props.sizeClassName ? this.props.sizeClassName : "medium";

        let imageUrl = this.props.imageUrl;
        if (!imageUrl && this.props.id) {
            imageUrl = getIdentityImageUrl(this.context.pageContext, this.props.id);
        }

        return (
            <div className="flex-grow flex-row rhythm-horizontal-8">
                <BuildPersona size={sizeClassName} displayName={this.props.displayName} imageUrl={imageUrl} />
                <div className="flex-column justify-center margin-left-4">
                    <span className="primary-row primary-text">
                        <div className="message">{this.props.getPrimaryElement(this.props.data, this.props.changeLink)}</div>
                        <span>{/*Empty span to trick the .message to honor ellipsis, see ellipsis-without-width mixin...*/}</span>
                    </span>
                    <span className="font-size padding-left-4 secondary-text">{this.props.children}</span>
                </div>
            </div>
        );
    }
}

interface ISmartCommitLinkProps {
    change: Change;
    changeLink: string;
    repositoryName: string;
    ariaDescribedBy: string;
    sourceProvider: ISourceProvider;
    textToDisplay: string;
}

class SmartCommitLink extends React.PureComponent<ISmartCommitLinkProps, {}> {
    constructor(props: ISmartCommitLinkProps) {
        super(props);
    }

    public render() {
        const displayText = this.props.textToDisplay;
        if (this.props.change.location) {
            return (
                <FPSLink
                    ariaDescribedBy={this.props.ariaDescribedBy}
                    className="bolt-table-link bolt-table-inline-link"
                    href={this.props.changeLink}
                    telemetrySource={telemetrySource}
                >
                    {displayText}
                </FPSLink>
            );
        } else {
            return <span>{displayText}</span>;
        }
    }
}
